/**
 * Cliente HTTP da API.
 *
 * **A sessao vive no cookie `HttpOnly` e em lugar nenhum mais.** Nao ha token em
 * `localStorage`, nem em `sessionStorage`, nem em variavel de modulo: o JavaScript
 * literalmente nao consegue ler a sessao, e por isso nao consegue vaza-la para um
 * log, para um crash report ou para o console de quem abre o DevTools.
 *
 * A consequencia que muda o desenho do aplicativo inteiro: **a unica fonte da
 * verdade sobre "estou logado" e `GET /api/auth/eu`**. Nao ha leitura sincrona de um
 * token, entao o boot e assincrono e o render guarda esse estado.
 *
 * **`credentials: "same-origin"` e obrigatorio** mesmo estando na mesma origem: sem
 * o parametro explicito o `fetch` omite credenciais, e o cookie de sessao nao
 * chega. Nao e paranoia: e o padrao da plataforma.
 */

import type { Usuario } from "./tipos";

/** Erro com o status e o `detail` do corpo, que a API sempre preenche em pt-BR. */
export class ErroDaApi extends Error {
  readonly status: number;
  /** `Retry-After` em segundos, so no 429. */
  readonly esperarSegundos: number | null;
  /**
   * A API pediu **reautenticacao**: 403 com o header `X-requer-reauth`.
   *
   * **E o header, e nao o status.** Um 403 sem o header e "sem permissao" -- a
   * pessoa nao pode, e nao adianta pedir senha. O `X-requer-reauth` diz "a sessao
   * existe, mas nao prova o bastante": e a diferenca entre "sessao expirada" e
   * "confirme de novo", e as duas precisam de telas diferentes.
   */
  readonly requerReautenticacao: boolean;

  /**
   * A API exige o segundo fator **antes de qualquer outra rota**: `403` com o
   * header `X-precisa-configurar-totp`.
   *
   * **E o unico 403 que a tela tem o que fazer com.** Os outros dizem "voce nao
   * pode", e nao ha para onde mandar a pessoa; este diz "o cadastro ainda nao
   * terminou", e o destino e a tela de setup. Sem ele, abrir uma tela privada
   * com o segundo fator pendente nao levava a lugar nenhum: a excecao subia de
   * dentro do desenho, e o que ficava na tela era o esqueleto estatico do
   * `index.html` -- sem erro visivel, e sem caminho de volta.
   */
  readonly precisaConfigurarTotp: boolean;

  constructor(
    status: number,
    detail: string,
    retryAfter: string | null,
    requerReautenticacao = false,
    precisaConfigurarTotp = false,
  ) {
    super(detail);
    this.name = "ErroDaApi";
    this.status = status;
    const segundos = Number.parseInt(retryAfter ?? "", 10);
    this.esperarSegundos = Number.isFinite(segundos) ? segundos : null;
    this.requerReautenticacao = requerReautenticacao;
    this.precisaConfigurarTotp = precisaConfigurarTotp;
  }
}

/**
 * Chamada de API que **sempre** devolve JSON ou lanca `ErroDaApi`.
 *
 * **O `catch` no `fetch` vira `ErroDaApi(0)` e nunca uma excecao crua.** Quem
 * trata erro de tela precisa de uma unica forma de verificar; e o `fetch` sem
 * tratamento devolve `TypeError: Failed to fetch`, que nao tem status e nao diz
 * se foi a rede, o Caddy ou o DNS.
 */
export async function chamar<T>(caminho: string, init: RequestInit = {}): Promise<T> {
  let resposta: Response;
  try {
    resposta = await fetch(caminho, {
      credentials: "same-origin",
      ...init,
      headers: {
        Accept: "application/json",
        ...(init.body === undefined ? {} : { "Content-Type": "application/json" }),
        ...init.headers,
      },
    });
  } catch (problema) {
    throw new ErroDaApi(0, "Sem conexao com a casa.", null);
  }

  if (resposta.status === 204) {
    return undefined as T;
  }

  const texto = await resposta.text();
  // Um `502` do proxy vem com HTML, e `JSON.parse` disso e uma excecao que
  // aponta para o cliente e nao para o proxy. O corpo ilegivel vira mensagem.
  let corpo: unknown = null;
  if (texto !== "") {
    try {
      corpo = JSON.parse(texto);
    } catch {
      corpo = null;
    }
  }

  if (!resposta.ok) {
    throw new ErroDaApi(
      resposta.status,
      detalheDe(corpo, resposta.status),
      resposta.headers.get("Retry-After"),
      resposta.headers.get("X-requer-reauth") === "true",
      resposta.headers.get("X-precisa-configurar-totp") === "true",
    );
  }

  return corpo as T;
}

function detalheDe(corpo: unknown, status: number): string {
  if (corpo !== null && typeof corpo === "object" && "detail" in corpo) {
    const detalhe = (corpo as { detail: unknown }).detail;
    // O FastAPI devolve `detail` como lista quando e um 422 de validacao, e a
    // lista nao serve para mostrar a alguem.
    if (typeof detalhe === "string" && detalhe !== "") {
      return detalhe;
    }
    // **`detail` tambem pode ser um objeto**, e nao so uma string: o `409` do
    // pedido de posicao devolve `{motivo, faltando}`. Sem esta linha a tela
    // mostraria "Falhou a requisicao (409)" -- que nao diz a pessoa se ela pode
    // repetir daqui a 30 s, nem que o aparelho nao tem caminho para responder.
    if (typeof detalhe === "object" && detalhe !== null && "motivo" in detalhe) {
      const motivo = (detalhe as { motivo: unknown }).motivo;
      if (typeof motivo === "string" && motivo !== "") {
        return motivo;
      }
    }
  }
  return status === 401 ? "Email ou senha invalidos." : `Falhou a requisicao (${status}).`;
}

/** `GET /api/auth/eu`, ou `null` quando nao ha sessao. */
export async function eu(): Promise<Usuario | null> {
  try {
    return await chamar<Usuario>("/api/auth/eu");
  } catch (erro) {
    // So o 401 vira "ninguem logado". Qualquer outro erro e problema de verdade
    // e engolir aqui seria mostrar a tela de acesso quando a casa esta fora.
    if (erro instanceof ErroDaApi && erro.status === 401) {
      return null;
    }
    throw erro;
  }
}

export async function entrar(email: string, senha: string, codigo: string | null): Promise<Usuario> {
  return chamar<Usuario>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, senha, codigo }),
  });
}

export async function sair(): Promise<void> {
  await chamar<void>("/api/auth/logout", { method: "POST" });
}

export async function aceitarConvite(
  token: string,
  nome: string,
  senha: string,
): Promise<Usuario> {
  return chamar<Usuario>("/api/auth/aceitar-convite", {
    method: "POST",
    body: JSON.stringify({ token, nome, senha }),
  });
}

export async function recuperar(email: string, senha: string, codigo: string): Promise<Usuario> {
  return chamar<Usuario>("/api/auth/recuperar", {
    method: "POST",
    body: JSON.stringify({ email, senha, codigo }),
  });
}

export async function reautenticar(senha: string, codigo: string | null): Promise<Usuario> {
  return chamar<Usuario>("/api/auth/reautenticar", {
    method: "POST",
    body: JSON.stringify({ senha, codigo }),
  });
}

export async function iniciarTotp(): Promise<{ uri: string; segredo: string }> {
  return chamar<{ uri: string; segredo: string }>("/api/auth/totp/iniciar", { method: "POST" });
}

/**
 * `GET /api/auth/totp`: o setup **em andamento**, sem gerar segredo novo.
 *
 * `null` quando nao ha setup pendente (o `404` da API). **E o que a tela usa
 * primeiro**, porque `iniciarTotp` regenera: chamado a cada desenho da tela,
 * ele invalidava o segredo que a pessoa acabou de cadastrar no autenticador.
 * No celular isso acontece de verdade -- sair para o autenticador e voltar
 * recarrega a aba.
 */
export async function totpPendente(): Promise<{ uri: string; segredo: string } | null> {
  try {
    return await chamar<{ uri: string; segredo: string }>("/api/auth/totp");
  } catch (erro) {
    // **So o 404 e "nao ha setup".** Qualquer outro erro e problema de
    // verdade, e transforma-lo em "comece um novo" geraria um segredo novo em
    // cima de um setup que existia.
    if (erro instanceof ErroDaApi && erro.status === 404) {
      return null;
    }
    throw erro;
  }
}

export async function confirmarTotp(codigo: string): Promise<{ codigos: string[]; aviso: string }> {
  return chamar<{ codigos: string[]; aviso: string }>("/api/auth/totp/confirmar", {
    method: "POST",
    body: JSON.stringify({ codigo }),
  });
}

// ------------------------------------------------------------- aparelhos

export interface Sessao {
  id: string;
  criada_em: string;
  ultimo_uso_em: string;
  expira_em: string;
  esta_atual: boolean;
}

export interface Aparelho {
  id: string;
  platform: string;
  label: string;
  mqtt_username: string;
  tem_credencial_mqtt: boolean;
  tem_token_de_atalhos: boolean;
  trigger_email: string | null;
  trigger_phone: string | null;
  compartilhamento_pausado: boolean;
  revogado_em: string | null;
  push_configurado: boolean;
}

export interface CredencialMqtt {
  mqtt_username: string;
  senha: string;
  aviso: string;
}

export async function sessoes(): Promise<Sessao[]> {
  return chamar<Sessao[]>("/api/sessoes");
}

export async function revogarSessao(sessaoId: string): Promise<void> {
  await chamar<void>(`/api/sessoes/${sessaoId}`, { method: "DELETE" });
}

export async function aparelhos(): Promise<Aparelho[]> {
  return chamar<Aparelho[]>("/api/aparelhos");
}

export async function cadastrarAparelho(platform: string, label: string): Promise<Aparelho> {
  return chamar<Aparelho>("/api/aparelhos", {
    method: "POST",
    body: JSON.stringify({ platform, label }),
  });
}

/**
 * Gera (ou troca) a senha MQTT. **A senha volta em claro uma unica vez.**
 *
 * Por isso o retorno e guardado em memoria pela tela e nunca vai para o
 * `localStorage`: o aparelho e o lugar onde a pessoa vai digitar a senha, e a
 * tela e o lugar onde ela vai ficar visivel por alguns segundos.
 */
export async function gerarCredencialMqtt(deviceId: string): Promise<CredencialMqtt> {
  return chamar<CredencialMqtt>(`/api/aparelhos/${deviceId}/mqtt`, { method: "POST" });
}

export async function definirAtalhos(
  deviceId: string,
  triggerEmail: string | null,
  triggerPhone: string | null,
): Promise<Aparelho> {
  return chamar<Aparelho>(`/api/aparelhos/${deviceId}/atalhos`, {
    method: "POST",
    body: JSON.stringify({ trigger_email: triggerEmail, trigger_phone: triggerPhone }),
  });
}

export async function registrarPush(deviceId: string, assinatura: unknown): Promise<Aparelho> {
  return chamar<Aparelho>(`/api/aparelhos/${deviceId}/push`, {
    method: "POST",
    body: JSON.stringify({ subscription: assinatura }),
  });
}

export async function chaveVapid(): Promise<string> {
  const chave = await chamar<{ public_key: string }>("/api/push/vapid");
  return chave.public_key;
}

export async function revogarAparelho(deviceId: string): Promise<void> {
  await chamar<void>(`/api/aparelhos/${deviceId}`, { method: "DELETE" });
}

// ------------------------------------------------------------------ alertas

export interface Alerta {
  id: string;
  kind: string;
  title: string;
  body: string;
  payload: Record<string, unknown>;
  criada_em: string;
  status: string;
  lido: boolean;
  lido_em: string | null;
}

export interface Central {
  alertas: Alerta[];
  nao_lidos: number;
}

export async function central(apenasNaoLidos = false): Promise<Central> {
  return chamar<Central>(`/api/alertas?apenas_nao_lidos=${apenasNaoLidos ? "true" : "false"}`);
}

export async function marcarLido(alertaId: string): Promise<Alerta> {
  return chamar<Alerta>(`/api/alertas/${alertaId}/lido`, { method: "POST" });
}

export async function marcarTodosLidos(): Promise<{ marcados: number }> {
  return chamar<{ marcados: number }>("/api/alertas/lidos", { method: "POST" });
}

// ------------------------------------------------------------------------- mapa

/**
 * Um ponto como o mapa recebe.
 *
 * **`idade_s` e calculada no servidor** e viaja com o ponto: um `recorded_at` só
 * obriga quem mostra a fazer a conta, e essa conta é onde "agora vira suposição"
 * -- com o relogio do aparelho errado, um ponto de duas horas vira "agora".
 *
 * `battery_percent` é `null` quando o OwnTracks não mandou, e `null` **não** é
 * `0`: ausencia de bateria é bateria desconhecida.
 */
export interface Ponto {
  device_id: string;
  lat: number;
  lon: number;
  recorded_at: string;
  idade_s: number;
  accuracy_meters: number | null;
  tst_ausente: boolean;
  battery_percent: number | null;
  /** De quem é o ponto. O item é "por pessoa": `device_id` não diz de quem. */
  device_label: string;
  dono_nome: string;
}

/** Um pedido no mapa. `status` é `pending`, `answered` ou `expired`. */
export interface Pedido {
  id: string;
  device_id: string;
  requested_by: string;
  requested_at: string;
  expires_at: string;
  status: string;
  method: string;
  origin: string;
  answered_at: string | null;
  answered_location_id: string | null;
}

/** O retrato inicial da stream: **um ponto por aparelho**, e os pedidos vivos. */
export interface Retrato {
  em: string;
  pontos: Ponto[];
  pedidos: Pedido[];
}

/** Uma linha do "quem pediu sua posição e quando". */
export interface PedidoHistorico {
  id: string;
  device_id: string;
  device_label: string;
  requested_by: string;
  solicitante_nome: string;
  requested_at: string;
  status: string;
  method: string;
  origin: string;
  answered_at: string | null;
  answered_location_id: string | null;
}

export async function historicoDePedidos(limite = 50): Promise<PedidoHistorico[]> {
  return chamar<PedidoHistorico[]>(`/api/location/requests/historico?limite=${limite}`);
}

/**
 * O que `POST /api/location/requests` devolve.
 *
 * **`frase_codigo` e `sms_para` so vem preenchidos no iPhone** -- no Android os
 * dois sao `null` e a tela nao mostra botao nenhum, porque o comando ja foi
 * publicado e o aparelho responde sozinho.
 *
 * **O `sms_para` e montado pelo servidor** e a tela abre o que veio: o telefone
 * esta no cadastro do aparelho e a frase esta no pedido, e nenhum dos dois esta
 * no estado da tela.
 */
export interface RespostaDePedido {
  pedido: Pedido;
  publicado: boolean;
  metodo: string;
  frase_codigo: string | null;
  sms_para: string | null;
}

/**
 * Pede a posicao de um aparelho. **Nao exige reautenticacao**, por decisao da
 * propria rota: pedir a posicao de alguem e uma acao, nao a leitura de um
 * segredo (ver a nota do modulo `routers/locations.py`).
 */
export async function pedirPosicao(deviceId: string): Promise<RespostaDePedido> {
  return chamar<RespostaDePedido>("/api/location/requests", {
    method: "POST",
    body: JSON.stringify({ device_id: deviceId }),
  });
}

/**
 * Pausa ou despausa o compartilhamento de um aparelho.
 *
 * **O corpo é o estado, e não o comando** -- é o que a API espera, e é o que faz
 * as duas direções passarem pelo mesmo caminho testado.
 */
export async function pausarCompartilhamento(
  deviceId: string,
  pausado: boolean,
): Promise<Aparelho> {
  return chamar<Aparelho>(`/api/aparelhos/${deviceId}/pausa`, {
    method: "PATCH",
    body: JSON.stringify({ pausado }),
  });
}